if (!customElements.get('wlsn-announcement-bar')) {
  class WLSNAnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'wlsn-hidden',
        swiperClass: 'js-announcement-bar-slider',
        prevButton: 'js-swiper-btn-prev',
        nextButton: 'js-swiper-btn-next',
        sectionId: `${this.getAttribute('data-section-id')}`,
      };

      /** @type {(() => void) | undefined} */
      this._onLayout = undefined;
      /** @type {MediaQueryList | undefined} */
      this._mql = undefined;
      /** @type {ResizeObserver | undefined} */
      this._resizeObserver = undefined;
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        swiperElement: this.querySelector(`.${this.classes.swiperClass}`),
        prevButton: this.querySelector(`.${this.classes.prevButton}`),
        nextButton: this.querySelector(`.${this.classes.nextButton}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this._onLayout = this._setAnnouncementBarHeight.bind(this);

      this._initializeSwiper();
      this._setAnnouncementBarHeight();

      this._mql = window.matchMedia('(max-width: 1023px)');
      this._mql.addEventListener('change', this._onLayout);
      window.addEventListener('scroll', this._onLayout, { passive: true });

      this._resizeObserver = new ResizeObserver(() => this._setAnnouncementBarHeight());
      this._resizeObserver.observe(this);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._setAnnouncementBarHeight());
      });
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      if (this._onLayout) {
        window.removeEventListener('scroll', this._onLayout);
        if (this._mql) {
          this._mql.removeEventListener('change', this._onLayout);
        }
      }
      this._resizeObserver?.disconnect();
      this._resizeObserver = undefined;
      this._mql = undefined;
      this._onLayout = undefined;
    }

    /**
     * Initialize Swiper carousel
     * @returns {void}
     */
    _initializeSwiper() {
      const settingsElement = document.getElementById(`CarouselSettings-${this.classes.sectionId}`);
      const settings = JSON.parse(settingsElement.textContent);

      this.swiper = new Swiper(this.elements.swiperElement, {
        loop: settings.loop,
        spaceBetween: 12,
        slidesPerView: 1,
        pagination: false,
        autoplay: {
          delay: settings.animationSpeed || 3000,
          enabled: settings.autoplay,
        },

        navigation: {
          prevEl: this.elements.prevButton,
          nextEl: this.elements.nextButton,
          enabled: true,
        },
      });

      if (!this.swiper.isLocked) {
        this.elements.prevButton.classList.remove(this.classes.hidden);
        this.elements.nextButton.classList.remove(this.classes.hidden);
      } else {
        this.elements.prevButton.classList.add(this.classes.hidden);
        this.elements.nextButton.classList.add(this.classes.hidden);
      }
    }

    /**
     * Visible height (px) of this bar measured from the viewport top: shrinks as the bar scrolls up
     * until its bottom edge passes y = 0.
     * @returns {number}
     */
    _getViewportVisibleHeightPx() {
      const rect = this.getBoundingClientRect();
      return Math.max(0, rect.bottom - Math.max(0, rect.top));
    }

    /**
     * Set announcement bar height CSS variable (visible portion vs viewport top)
     * @returns {void}
     */
    _setAnnouncementBarHeight() {
      const announcementBarHeightPx = this._getViewportVisibleHeightPx();
      document.documentElement.style.setProperty(
        '--wlsn-announcement-bar-height',
        `${announcementBarHeightPx / 10}rem`
      );
    }
  }

  window.customElements.define('wlsn-announcement-bar', WLSNAnnouncementBar);
}
